import { useRef, useCallback } from 'react'
import { useJsApiLoader, Autocomplete } from '@react-google-maps/api'
import { Input } from '@/components/ui/input'
import { Loader2, Plane } from 'lucide-react'
import { GOOGLE_MAPS_LOADER_OPTIONS } from '@/lib/maps/loader'

export interface AirportSearchResult {
  readonly name: string
  readonly address: string
  readonly lat: number
  readonly lng: number
}

interface AirportSearchInputProps {
  readonly onSelect: (result: AirportSearchResult) => void
  readonly disabled?: boolean
}

export function AirportSearchInput({
  onSelect,
  disabled = false,
}: AirportSearchInputProps) {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  // Options MUST match every other Builder useJsApiLoader call site
  // (see src/lib/maps/loader.ts) or the loader throws
  // "Loader must not be called again with different options".
  const { isLoaded, loadError } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS)

  const onAutocompleteLoad = useCallback(
    (autocomplete: google.maps.places.Autocomplete) => {
      autocompleteRef.current = autocomplete
    },
    []
  )

  const onPlaceChanged = useCallback(() => {
    const autocomplete = autocompleteRef.current
    if (!autocomplete) return

    const place = autocomplete.getPlace()
    if (!place.geometry?.location) return

    const lat = place.geometry.location.lat()
    const lng = place.geometry.location.lng()
    const name = place.name || ''
    const address = place.formatted_address || ''

    onSelect({ name, address, lat, lng })

    // Clear input after successful selection
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }, [onSelect])

  if (loadError) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>Failed to load Google Maps. Please check your API key.</span>
      </div>
    )
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm text-muted-foreground">Loading airport search...</span>
      </div>
    )
  }

  return (
    <Autocomplete
      onLoad={onAutocompleteLoad}
      onPlaceChanged={onPlaceChanged}
      options={{
        types: ['airport'],
      }}
    >
      <div className="relative">
        <Plane className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search for an airport..."
          disabled={disabled}
          className="pl-9"
        />
      </div>
    </Autocomplete>
  )
}
